<template>
  <div class="strict-profile-card bg-primary relative">
    
    <!-- CUSTOM CLOSE BUTTON (Now wired to the bulletproof Vue method) -->
    <div class="main-card-close" @click="closeMainCard">
      <feather-icon icon="XIcon" class="w-5 h-5" />
    </div>

    <!-- Top Section: Avatar & Info (Centered) -->
    <div class="flex flex-col items-center text-center mt-4">
      <div style="position: relative; margin-bottom: 1rem;">
        <vs-avatar
          v-if="boardUserData.userData.profilePictureUrl"
          class="profile-picture"
          size="120px"
          :src="
            constants.s3Bucket +
            '/editor/profile-pictures/' +
            boardUserData.userData.profilePictureUrl
          "
        />
        <vs-avatar
          v-else
          class="profile-picture"
          size="120px"
          :src="`https://hostedboringavatars.vercel.app/api/beam?name=${avatarNameGenerator(
            boardUserData.userData.firstName || 'boringAvatar',
            boardUserData.userData.lastName || 'boringAvatar'
          )}`"
        />
      </div>
      
      <div class="mb-base w-full">
        <div class="name text-white">
          {{ boardUserData.userData.firstName | title }}
          {{ boardUserData.userData.lastName | title }} 
        </div>
        <div v-if="userData.email" class="text-white mb-3 lowercase">
          {{ boardUserData.userData.email | title }}
        </div>
        <div v-if="userData.roleTitle" class="role-title my-4 text-white">
          {{ boardUserData.roleTitle.label || boardUserData.roleTitle }}
        </div>
        
        <!-- SOCIAL & ACTION ICONS -->
        <vs-row class="items-center mt-4" style="justify-content: center">
          
          <!-- 1. TALENT CARD BUTTON -->
          <div class="flex mr-2 items-center" style="justify-content: center">
            <vs-tooltip class="cursor-pointer" text="View Talent Card">
              <vs-button
                radius
                color="white"
                text-color="grey"
                type="filled"
                icon-pack="feather"
                icon="icon-user"
                :disabled="isLoading"
                @click.stop="openTalentCard"
              ></vs-button>
            </vs-tooltip>
          </div>

          <!-- 2. LINKEDIN -->
          <div
            v-if="boardUserData.userData && boardUserData.userData.linkedinProfile"
            class="flex mr-2 items-center"
            style="justify-content: center"
          >
            <vs-tooltip class="cursor-pointer" text="LinkedIn">
              <vs-button
                radius
                color="white"
                text-color="grey"
                type="filled"
                icon-pack="feather"
                icon="icon-linkedin"
                :href="boardUserData.userData.linkedinProfile"
                target="_blank"
              ></vs-button>
            </vs-tooltip>
          </div>

          <!-- 3. FACEBOOK -->
          <div
            v-if="boardUserData.userData && boardUserData.userData.facebookProfile"
            class="flex mr-2 items-center"
            style="justify-content: center"
          >
            <vs-tooltip class="cursor-pointer" text="Facebook">
              <vs-button
                radius
                color="white"
                text-color="grey"
                type="filled"
                icon-pack="feather"
                icon="icon-facebook"
                :href="boardUserData.userData.facebookProfile"
                target="_blank"
              ></vs-button>
            </vs-tooltip>
          </div>
          
          <!-- 4. TWITTER -->
          <div
            v-if="boardUserData.userData && boardUserData.userData.twitterProfile"
            class="flex mr-2 items-center"
            style="justify-content: center"
          >
            <vs-tooltip class="cursor-pointer" text="Twitter">
              <vs-button
                radius
                color="white"
                text-color="grey"
                type="filled"
                icon-pack="feather"
                icon="icon-twitter"
                :href="boardUserData.userData.twitterProfile"
                target="_blank"
              ></vs-button>
            </vs-tooltip>
          </div>
          
          <!-- 5. PHONE -->
          <div
            v-if="boardUserData.userData && boardUserData.userData.contactNumber"
            class="flex mr-2 items-center"
            style="justify-content: center"
          >
            <vs-tooltip class="cursor-pointer" text="WhatsApp">
              <vs-button
                radius
                color="white"
                text-color="grey"
                type="filled"
                icon-pack="feather"
                icon="icon-phone"
                :href="`https://wa.me/${boardUserData.userData.contactNumber}`"
                target="_blank"
              ></vs-button>
            </vs-tooltip>
          </div>

          <!-- 6. EMAIL -->
          <div
            v-if="boardUserData.userData && boardUserData.userData.email"
            class="flex items-center"
            style="justify-content: center"
          >
            <vs-tooltip class="cursor-pointer" text="Email">
              <a
                :href="`mailto:${boardUserData.userData.email}`"
                target="_blank"
              >
                <vs-button
                  radius
                  color="white"
                  text-color="grey"
                  type="filled"
                  icon="mail"
                ></vs-button>
              </a>
            </vs-tooltip>
          </div>
        </vs-row>
      </div>
    </div>

    <vs-divider class="my-base" color="white" />

    <!-- ABOUT SECTION -->
    <div class="my-base text-left" v-if="boardUserData.userData.about">
      <h6 class="heading mb-2 text-white">About</h6>
      <p class="content text-white-o8">
        {{ boardUserData.userData.about }}
      </p>
    </div>

    <!-- ATTACHMENTS SECTION -->
    <div class="my-base text-left" v-if="hasAttachments">
      <h6 class="heading mb-2 text-white">Attachments</h6>
      <div v-for="(file, idx) in availableAttachments" :key="idx" class="flex items-center gap-2 mb-1">
        <feather-icon icon="PaperclipIcon" class="w-4 h-4 text-white" />
        <a :href="file.url || file" target="_blank" class="text-white-o8 underline content">
          {{ file.name || 'View Attached Document' }}
        </a>
      </div>
    </div>

    <!-- TALENT CARD POPUP COMPONENT (Embedded) -->
    <vs-popup class="talent-card-popup" title="" :active.sync="isPopupActive">
      
      <div class="card-close-icon" @click="isPopupActive = false">
        <feather-icon icon="XIcon" class="w-6 h-6 cursor-pointer" />
      </div>

      <div v-if="isLoading" class="loading-state">
        <div class="con-vs-loading"><div class="vs-loading"></div></div>
        <p>Fetching Profile...</p>
      </div>

      <div v-else-if="profileData" class="card-container">
        <!-- Header Section -->
        <div class="card-header">
          <div class="avatar-wrapper">
             <vs-avatar 
               v-if="profileData.profilePictureUrl"
               size="100px"
               class="tc-avatar-component"
               :src="constants.s3Bucket + '/editor/profile-pictures/' + profileData.profilePictureUrl" 
             />
             <vs-avatar 
               v-else 
               size="100px"
               class="tc-avatar-component"
               :src="`https://hostedboringavatars.vercel.app/api/beam?name=${profileData.firstName}+${profileData.lastName}&colors=F2C244,E77E23,7367F0,28C76F,EA5455`" 
             />
          </div>
        </div>

        <!-- Card Body -->
        <div class="card-body">
          <h2 class="user-name">{{ profileData.firstName }} {{ profileData.lastName }}</h2>
          <p class="user-role">{{ profileData.profession || 'Candidate' }}</p>

          <div class="divider"></div>

          <!-- CAREER OBJECTIVE -->
          <div class="info-section">
            <h5 class="section-label">Career Objective</h5>
            <p class="objective-text">
              {{ profileData.careerObjective || 'No career objective provided.' }}
            </p>
          </div>

          <!-- CONTACT INFO -->
          <div class="info-section contact-info">
            <h5 class="section-label">Contact Info</h5>

            <div class="contact-row" v-if="profileData.address">
              <feather-icon icon="MapPinIcon" class="icon-small" />
              <span>{{ profileData.address }}</span>
            </div>

            <!-- PHONE & SOCIAL ICONS -->
            <div class="contact-row" v-if="profileData.contactNumber">
              <feather-icon icon="PhoneIcon" class="icon-small" />
              <span style="margin-right: 12px;">{{ profileData.contactNumber }}</span>
              
              <div class="social-actions">
                <a :href="getWhatsAppLink(profileData.contactNumber)" target="_blank" title="Message on WhatsApp" class="social-icon whatsapp">
                  <svg style="width:18px;height:18px;" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                  </svg>
                </a>
                <a :href="getTelegramLink(profileData.contactNumber)" target="_blank" title="Message on Telegram" class="social-icon telegram">
                  <svg style="width:18px;height:18px;" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.504 8.527l-1.854 8.74c-.139.63-.513.784-1.04.488l-2.875-2.119-1.388 1.336c-.153.153-.282.282-.578.282l.207-2.913 5.305-4.791c.231-.205-.05-.319-.358-.113l-6.557 4.126-2.825-.883c-.614-.192-.627-.614.128-.909l11.04-4.254c.511-.192.964.116.804.91z"/>
                  </svg>
                </a>
              </div>
            </div>

            <!-- EMAIL LINK -->
            <div class="contact-row">
              <feather-icon icon="MailIcon" class="icon-small" />
              <a :href="'mailto:' + (profileData.email || userData.email)" class="email-link" title="Send Email">
                {{ profileData.email || userData.email }}
              </a>
            </div>
          </div>

          <!-- SKILLS -->
          <div class="info-section" v-if="formattedSkills.length > 0">
            <h5 class="section-label">Skills</h5>
            <div class="skills-container">
              <span v-for="(skill, i) in formattedSkills" :key="i" class="skill-chip">
                {{ skill }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import allLanguages from "@/assets/data/all-languages.js";
import marketplaceList from "@/assets/data/marketplace-list.js";
import constants from "@/constants";
import organizationConfig from "@/assets/data/organization-config.js";

const API_BASE_URL = "https://api.getboarded.tech";

export default {
  props: {
    boardUserData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      allLanguages,
      marketplaceList,
      constants,
      organizationConfig,
      abilities: [],
      
      // Talent Card Data
      isPopupActive: false,
      isLoading: false,
      profileData: null,
    };
  },
  computed: {
    userData() {
      return this.boardUserData.userData;
    },
    avatarNameGenerator() {
      return this.$store.state.users.avatarNameGenerator;
    },
    locale() {
      return this.$i18n.locale;
    },
    hasAttachments() {
      return (this.boardUserData.attachments && this.boardUserData.attachments.length > 0) || 
             (this.boardUserData.userData && this.boardUserData.userData.attachments && this.boardUserData.userData.attachments.length > 0);
    },
    availableAttachments() {
      if (this.boardUserData.attachments && this.boardUserData.attachments.length > 0) {
        return this.boardUserData.attachments;
      }
      if (this.boardUserData.userData && this.boardUserData.userData.attachments) {
        return this.boardUserData.userData.attachments;
      }
      return [];
    },
    formattedSkills() {
      if (!this.profileData) return [];
      if (this.profileData.skills && this.profileData.skills.length) {
         return this.profileData.skills.map(s => s.title || s);
      }
      if (this.profileData.extraCurricular && this.profileData.extraCurricular.length > 0) {
        const ec = this.profileData.extraCurricular[0];
        if (ec.skills && Array.isArray(ec.skills)) {
          return ec.skills.map(s => s.title || s);
        }
      }
      return [];
    }
  },
  watch: {
    boardUserData(newVal, oldVal) {
      if (newVal && oldVal && newVal.userId !== oldVal.userId) {
        this.profileData = null;
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      // Find the direct parent popup content for THIS card
      const contentEl = this.$el.closest('.vs-popup--content');
      
      if (contentEl) {
        // AGGRESSIVE CSS OVERRIDES: Force parent to have zero styling
        contentEl.style.setProperty('background', 'transparent', 'important');
        contentEl.style.setProperty('padding', '0', 'important');
        contentEl.style.setProperty('margin', '0', 'important');
        contentEl.style.setProperty('box-shadow', 'none', 'important');
        
        const popupMain = contentEl.closest('.vs-popup');
        if (popupMain) {
          popupMain.style.setProperty('background', 'transparent', 'important');
          popupMain.style.setProperty('border', 'none', 'important');
          popupMain.style.setProperty('box-shadow', 'none', 'important');
        }

        const popupHeader = contentEl.closest('.con-vs-popup')?.querySelector('.vs-popup--header');
        if (popupHeader) {
          popupHeader.style.setProperty('display', 'none', 'important');
        }
      }
    });
  },
  methods: {
    // BULLETPROOF CLOSE METHOD
    closeMainCard() {
      // 1. Emit standard close event (just in case parent uses @close)
      this.$emit('close'); 

      // 2. Traverse the Vue component tree to find the parent Vuesax popup and shut it down from the inside.
      let parent = this.$parent;
      while (parent) {
        // Check if the component tag is vs-popup
        if (parent.$options._componentTag === 'vs-popup' || (parent.$el && parent.$el.classList && parent.$el.classList.contains('vs-popup'))) {
          // Vuesax uses 'update:active' to close via v-model or .sync
          parent.$emit('update:active', false);
          return; // Stop once we successfully tell the popup to close
        }
        parent = parent.$parent;
      }

      // 3. Absolute Fallback: Click the hidden close button if Vue traverse fails
      const closeBtn = this.$el.closest('.con-vs-popup')?.querySelector('.vs-popup--close');
      if (closeBtn) {
        closeBtn.click();
      }
    },

    getWhatsAppLink(phone) {
      if (!phone) return '#';
      const cleaned = phone.toString().replace(/\D/g, '');
      return `https://wa.me/${cleaned}`;
    },
    getTelegramLink(phone) {
      if (!phone) return '#';
      const cleaned = phone.toString().replace(/[\s\-\(\)]/g, '');
      const formatted = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
      return `https://t.me/${formatted}`;
    },
    async openTalentCard() {
      const targetUserId = this.boardUserData.userId || (this.boardUserData.userData && this.boardUserData.userData._id);
      if (!targetUserId) return;
      
      this.isPopupActive = true;
      if (this.profileData) return;

      this.isLoading = true;
      try {
        const url = `${API_BASE_URL}/users/${targetUserId}`;
        const token = localStorage.getItem('accessToken') || this.$store.state.bearerToken;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) throw new Error('API Error');
        this.profileData = await response.json();
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* MAIN YELLOW CARD STYLES */
.strict-profile-card {
  width: 420px !important;
  max-width: 100vw;           
  margin: 0 auto;             
  border-radius: 16px;        
  padding: 2.5rem 2rem;       
  box-sizing: border-box;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  position: relative; 
}

/* CUSTOM CLOSE BUTTON STYLES */
.main-card-close {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 100;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.1); 
  border-radius: 50%;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.main-card-close:hover {
  background: rgba(0, 0, 0, 0.25);
  color: #ffffff;
  transform: scale(1.05);
}

/* EXISTING STYLES */
.text-white-o8 {
  color: rgba(255, 255, 255, 0.8) !important;
}
.profile-picture {
  border: 5px solid #ffffff !important;
}
.name {
  font-weight: 600;
  font-size: 25px;
  line-height: 41px;
  letter-spacing: -0.02em;
}
.email {
  font-size: 12px;
}
.current-location {
  font-size: 12px;
}
.role-title {
  font-size: 15px;
}
.content {
  font-size: 12px;
  line-height: 23px;
}
.con-vs-avatar >>> img {
  object-fit: cover;
}

/* TALENT CARD POPUP STYLES */
.talent-card-popup {
  z-index: 100000 !important;
}

.talent-card-popup >>> .vs-popup {
  width: 400px !important;
  background: transparent !important;
  box-shadow: none !important;
}

.talent-card-popup >>> .vs-popup--header {
  display: none !important;
}

.talent-card-popup >>> .vs-popup--content {
  padding: 0 !important;
  border-radius: 20px;
  overflow: hidden;
  background: white !important; /* Force white for inner talent card */
  position: relative;
  max-height: 80vh;           
  overflow-y: auto;           
  scrollbar-width: none;      
  -ms-overflow-style: none;  
}

.talent-card-popup >>> .vs-popup--content::-webkit-scrollbar {
  display: none;
}

.card-close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 100;
  color: #626262;
  background: rgba(255,255,255,0.4);
  border-radius: 50%;
  padding: 4px;
}

.card-container {
  display: flex;
  flex-direction: column;
}

.card-header {
  height: 100px;
  background: #fdfdfd;
  display: flex;
  justify-content: center;
  position: relative;
}

.avatar-wrapper {
  position: absolute;
  bottom: -50px;
  z-index: 10;
}

.tc-avatar-component {
  border: 5px solid white !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.card-body {
  background: linear-gradient(135deg, #7367F0 0%, #9E95F5 100%);
  color: white;
  padding: 65px 30px 40px 30px;
  text-align: left;
}

.user-name {
  text-align: center;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.user-role {
  text-align: center;
  font-size: 1rem;
  opacity: 0.9;
  margin-top: 4px;
  margin-bottom: 25px;
}

.divider {
  height: 1px;
  background: rgba(255,255,255,0.25);
  margin-bottom: 25px;
}

.info-section {
  margin-bottom: 25px;
}

.section-label {
  font-size: 0.75rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.7);
  font-weight: 600;
  margin-bottom: 10px;
}

.objective-text {
  font-size: 0.9rem;
  line-height: 1.6;
}

.contact-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.social-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.social-icon {
  color: white;
  transition: all 0.2s ease;
  cursor: pointer;
}

.social-icon:hover {
  transform: scale(1.15);
}

.social-icon.whatsapp:hover {
  color: #25D366;
}

.social-icon.telegram:hover {
  color: #229ED9;
}

.email-link {
  color: white;
  text-decoration: none;
  transition: opacity 0.2s;
}

.email-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.icon-small {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  opacity: 0.8;
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-chip {
  background: rgba(255,255,255,0.25);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
}

.loading-state {
  padding: 60px;
  text-align: center;
  color: #7367F0;
}
</style>